import { AlertCircle, Package } from "lucide-react";

import type { Pro014StepFourItem } from "@/@types/campaign";
import type { CampaignConflictItem } from "@/@types/campaign-conflict";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { COMMON_TEXTS } from "../../../utils/messages";
import { CONFLICT_FILTER, PRO014_MIN_ITEMS, PRO014_PRODUCTS_TEXTS } from "../../constants";
import type { ConflictFilter } from "../../types";
import { Pro014ProductRow } from "./product-row";

const CONFLICT_FILTER_OPTIONS: Array<{ value: ConflictFilter; label: string }> = [
  { value: CONFLICT_FILTER.ALL, label: PRO014_PRODUCTS_TEXTS.FILTER_ALL },
  { value: CONFLICT_FILTER.CLEAN, label: PRO014_PRODUCTS_TEXTS.FILTER_CLEAN },
  { value: CONFLICT_FILTER.CONFLICT, label: PRO014_PRODUCTS_TEXTS.FILTER_CONFLICT },
];

interface ProductsListProps {
  totalItems: number;
  pageItems: Pro014StepFourItem[];
  visibleCount: number;
  pageOffset: number;
  currentPage: number;
  totalPages: number;
  conflictByCode: Map<string, CampaignConflictItem>;
  hasConflicts: boolean;
  conflictFilter: ConflictFilter;
  searchTerm: string;
  gradeRestricted: boolean;
  isReadOnly: boolean;
  itemsError?: string;
  onSearchChange: (value: string) => void;
  onConflictFilterChange: (value: ConflictFilter) => void;
  onGoToPage: (page: number) => void;
  onRemoveConflicting: () => void;
  onRemoveItem: (sku: string) => void;
  onEditGrade: (item: Pro014StepFourItem) => void;
  onRemoveFromPrevious?: (conflict: CampaignConflictItem) => void;
}

export function ProductsList({
  totalItems,
  pageItems,
  visibleCount,
  pageOffset,
  currentPage,
  totalPages,
  conflictByCode,
  hasConflicts,
  conflictFilter,
  searchTerm,
  gradeRestricted,
  isReadOnly,
  itemsError,
  onSearchChange,
  onConflictFilterChange,
  onGoToPage,
  onRemoveConflicting,
  onRemoveItem,
  onEditGrade,
  onRemoveFromPrevious,
}: ProductsListProps): JSX.Element {
  const lastShown: number = Math.min(pageOffset + pageItems.length, visibleCount);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="text-sm font-medium">{PRO014_PRODUCTS_TEXTS.SELECTED_TITLE}</Label>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {hasConflicts && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRemoveConflicting}
              disabled={isReadOnly}
              className="h-7 text-destructive hover:text-destructive"
            >
              {PRO014_PRODUCTS_TEXTS.REMOVE_CONFLICTING}
            </Button>
          )}
          <Badge variant={totalItems >= PRO014_MIN_ITEMS ? "default" : "destructive"}>
            {PRO014_PRODUCTS_TEXTS.PRODUCTS_COUNT(totalItems)}
          </Badge>
        </div>
      </div>

      <div className="flex flex-col gap-2 md:flex-row">
        <Input
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={PRO014_PRODUCTS_TEXTS.SEARCH_PLACEHOLDER}
          aria-label={PRO014_PRODUCTS_TEXTS.SEARCH_PLACEHOLDER}
          className="h-9 md:max-w-xs"
        />
        <Select
          value={conflictFilter}
          onValueChange={(value: string) => onConflictFilterChange(value as ConflictFilter)}
        >
          <SelectTrigger className="h-9 w-full md:w-44" aria-label={PRO014_PRODUCTS_TEXTS.FILTER_ARIA}>
            <SelectValue placeholder={PRO014_PRODUCTS_TEXTS.FILTER_PLACEHOLDER} />
          </SelectTrigger>
          <SelectContent>
            {CONFLICT_FILTER_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {totalItems === 0 && (
        <div className="rounded-lg border bg-gray-50 p-8 text-center">
          <Package className="mx-auto mb-2 h-12 w-12 text-muted-foreground opacity-50" aria-hidden />
          <p className="mb-1 text-sm text-muted-foreground">{PRO014_PRODUCTS_TEXTS.EMPTY_TITLE}</p>
          <p className="text-xs text-muted-foreground">
            {PRO014_PRODUCTS_TEXTS.EMPTY_HINT(PRO014_MIN_ITEMS)}
          </p>
        </div>
      )}

      {totalItems > 0 && (
        <div className="scrollbar-thin h-[400px] overflow-y-auto rounded-lg border bg-gray-50">
          <div className="space-y-2 p-3">
            {pageItems.map((item: Pro014StepFourItem, index: number) => (
              <Pro014ProductRow
                key={item.sku}
                item={item}
                position={pageOffset + index + 1}
                conflict={conflictByCode.get(item.sku)}
                gradeRestricted={gradeRestricted}
                isReadOnly={isReadOnly}
                onEditGrade={onEditGrade}
                onRemove={onRemoveItem}
                onRemoveFromPrevious={onRemoveFromPrevious}
              />
            ))}
            {visibleCount === 0 && (
              <div className="rounded-lg border bg-white p-8 text-center">
                <Package className="mx-auto mb-2 h-10 w-10 text-muted-foreground opacity-50" aria-hidden />
                <p className="text-sm text-muted-foreground">{PRO014_PRODUCTS_TEXTS.NOT_FOUND}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {totalItems > 0 && visibleCount > 0 && (
        <div className="flex flex-col items-center justify-between gap-2 text-xs text-muted-foreground md:flex-row">
          <span>{COMMON_TEXTS.SHOWING(pageOffset + 1, lastShown, visibleCount)}</span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              disabled={currentPage <= 1}
              onClick={() => onGoToPage(currentPage - 1)}
            >
              {PRO014_PRODUCTS_TEXTS.PREVIOUS}
            </Button>
            <span>{COMMON_TEXTS.PAGE(currentPage, totalPages)}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              disabled={currentPage >= totalPages}
              onClick={() => onGoToPage(currentPage + 1)}
            >
              {PRO014_PRODUCTS_TEXTS.NEXT}
            </Button>
          </div>
        </div>
      )}

      {itemsError && (
        <p className="flex items-center gap-1 text-sm text-destructive">
          <AlertCircle className="h-3 w-3" aria-hidden />
          {itemsError}
        </p>
      )}
    </div>
  );
}
